import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

type CampaignCardBadgeProps = {
  large?: boolean;
  medium?: boolean;
  small?: boolean;
  withImage?: boolean;
  badgeSale?: boolean;
  badgePromotion?: boolean;
  badgePreText?: string;
  badgeNews?: boolean;
  badgeText?: string;
  blackweek?: boolean;
  blackweekLogoLight?: boolean;
  blackweekLogoDark?: boolean;
  alternateLargeStyle?: boolean;
  badgeSaleRed?: boolean;
  badgeSalgLogo?: boolean;
  customBrandingLogo?: boolean;
  topLeftBranding?: any;
  saleWithExtraBubble?: boolean;
  smallBadgeText?: string;
  parentType?: 'v1' | 'v2';
};

const calculateBadgeMask = (
  blackweek?: boolean,
  blackweekLogoLight?: boolean,
  blackweekLogoDark?: boolean,
  salgLogo?: boolean,
  customBrandingLogo?: boolean,
  topLeftBranding?: any
) => {
  if (blackweek && !blackweekLogoLight && !blackweekLogoDark) {
    return {
      backgroundImage: 'url(/gfx/blackweek-bg.svg)',
      backgroundSize: '100% 100%',
      backgroundRepeat: 'no-repeat',
      backgroundColor: 'transparent',
    };
  }

  if (blackweekLogoLight) {
    return {
      backgroundImage: 'url(/gfx/blackweek-logo-light.svg)',
      backgroundSize: '100% 100%',
      backgroundRepeat: 'no-repeat',
      backgroundColor: 'transparent',
    };
  }

  if (blackweekLogoDark) {
    return {
      backgroundImage: 'url(/gfx/blackweek-logo-dark.svg)',
      backgroundSize: '100% 100%',
      backgroundRepeat: 'no-repeat',
      backgroundColor: 'transparent',
    };
  }

  if (salgLogo) {
    return {
      backgroundImage: 'url(/gfx/salgsalgsalg.svg)',
      backgroundSize: '100% 100%',
      backgroundRepeat: 'no-repeat',
      backgroundColor: 'transparent',
    };
  }

  if (customBrandingLogo && topLeftBranding) {
    return {
      backgroundImage: `url(${topLeftBranding.croppedImageUrl})`,
      backgroundSize: '80% 80%',
      backgroundPosition: 'center center',
      backgroundRepeat: 'no-repeat',
      backgroundColor: 'transparent',
    };
  }

  return {};
};

export const CampaignCardBadge = ({
  large,
  medium,
  small,
  withImage,
  badgeSale,
  badgePromotion,
  badgeNews,
  badgePreText,
  badgeText,
  blackweek,
  blackweekLogoLight,
  blackweekLogoDark,
  parentType,
  badgeSaleRed,
  badgeSalgLogo,
  customBrandingLogo,
  topLeftBranding,
  saleWithExtraBubble,
  smallBadgeText,
}: CampaignCardBadgeProps) => {
  let hideBadgeText =
    blackweekLogoLight ||
    blackweekLogoDark ||
    badgeSalgLogo ||
    customBrandingLogo;

  const badgeTextLength = badgeText?.length || 0;

  if (badgeNews) {
    badgeSaleRed = true;
    hideBadgeText = true;
  }

  return (
    <span
      style={calculateBadgeMask(
        blackweek,
        blackweekLogoLight,
        blackweekLogoDark,
        badgeSalgLogo,
        customBrandingLogo,
        topLeftBranding
      )}
      className={twMerge(
        clsx(
          'absolute flex flex-col justify-center items-center top-2 left-2',
          {
            'tracking-tighter leading-negative size-16 font-heading font-medium text-black-friday-badge text-neutral-80 @xs:size-24 @xs:text-[2rem] @lg:size-40 @lg:text-[4rem]':
              blackweek || badgeSaleRed || badgeSalgLogo || customBrandingLogo,
            'top-8 left-3': saleWithExtraBubble,
            'rounded-full bg-primary-50 text-neutral-0 @xs:size-24 @xs:text-[2rem] @lg:size-32 @lg:text-[4rem]':
              badgeSaleRed,
            'text-[1rem] @xs:text-[1.5rem] @lg:text-[2.5rem]':
              (blackweek ||
                badgeSaleRed ||
                badgeSalgLogo ||
                customBrandingLogo) &&
              badgeTextLength > 3,
            'rounded-full size-16 hd-sm bg-neutral-80 text-neutral-0 @xs:size-20 @xs:hd-md @lg:size-36 @lg:hd-2xl':
              badgeSale || saleWithExtraBubble,
            'bg-secondary-60 text-neutral-0 @lg:text-[2.5rem]': badgeNews,
            'rounded-full size-16 hd-lg bg-primary-50 text-neutral-0 promotion-text-shadow':
              badgePromotion,
            'origin-top-left scale-125 sm:scale-150':
              parentType === 'v1' &&
              (blackweek ||
                badgeSaleRed ||
                badgeSalgLogo ||
                customBrandingLogo),
            'origin-top-left sm:scale-125 md:scale-[1.3] lg:scale-150':
              parentType === 'v2' &&
              (blackweek ||
                badgeSaleRed ||
                badgeSalgLogo ||
                customBrandingLogo) &&
              large,
          }
        )
      )}
    >
      {saleWithExtraBubble && smallBadgeText && (
        <span className="-tracking-normal absolute -top-4 -right-4 @lg:-top-6 @lg:-right-6 size-3/6 bg-primary-50 text-neutral-0 text-sm @lg:text-lg rounded-full flex justify-center items-center">
          <span
            className={twMerge(
              clsx({ 'scale-75': smallBadgeText.length >= 6 })
            )}
          >
            {smallBadgeText}
          </span>
        </span>
      )}
      {badgePreText && !hideBadgeText && (
        <span
          className={twMerge(
            clsx('block hd-base tracking-normal', {
              'text-xxxs @sm:text-xxs @lg:text-sm font-semibold self-end pr-3 @sm:pr-5 @lg:pr-10':
                blackweek || badgeSaleRed,
            })
          )}
        >
          {badgePreText}
        </span>
      )}
      {badgeNews && 'NYHET'}
      {badgePromotion && (
        <div>
          <div
            className="font-light text-sm md:text-md uppercase"
            style={{ textShadow: 'none' }}
          >
            Medlems-
          </div>
          KUPP!
        </div>
      )}
      {(badgeSale || blackweek || badgeSaleRed || saleWithExtraBubble) &&
        !hideBadgeText &&
        badgeText}
    </span>
  );
};
